import Vue from "vue";
import router from "@/router";
import axios from "axios";
import store from "@/core/services/store";
import VueAxios from "vue-axios";
import JwtService from "@/core/services/jwt.service";
import { validationError } from "@/core/lib/common.lib";
import { ERROR } from "@/core/services/store/common.module";

/**
 * Service to call HTTP request via Axios
 */
const ApiService = {
	init() {
		Vue.prototype.$axiosInstance = axios.create();

		Vue.use(VueAxios, axios);
		Vue.axios.defaults.baseURL = process.env.VUE_APP_API_URL;
		Vue.axios.defaults.headers.common["Accept"] = "application/json";
		// Vue.axios.defaults.headers.common["Content-Type"] = "application/json";
		Vue.axios.defaults.trailingSlash = false;

		// Add a request interceptor
		Vue.axios.interceptors.request.use(
			function (config) {
				if (config.url.endsWith("/")) {
					config.url = config.url.slice(0, -1);
				}
				return config;
			},
			function (error) {
				return Promise.reject(error);
			}
		);

		// Add a response interceptor
		Vue.axios.interceptors.response.use(
			function (response) {
				return response;
			},
			function (error) {
				const response = error.response;
				if (response.status == 422) {
					const errors = validationError(response);
					store.dispatch(ERROR, errors);
				} else if (response.status == 401) {
					JwtService.destroyToken();
					const resolved = router.resolve({ name: "login" });
					window.location.href = resolved.href;
				} else {
					if (response.data && response.data.message) {
						store.dispatch(ERROR, [{ model: true, message: response.data.message, timeout: 2000 }]);
					} else {
						store.dispatch(ERROR, [
							{ model: true, message: "Something went wrong. Please try again later.", timeout: 2000 },
						]);
					}
				}
				return Promise.reject(error);
			}
		);
	},

	/**
	 * Set the default HTTP request headers
	 */
	setHeader() {
		Vue.axios.defaults.headers.common[
			"Authorization"
		] = `${JwtService.getTokenType()} ${JwtService.getToken()}`;
	},

	query(resource, params) {
		return new Promise((resolve, reject) => {
			Vue.axios
				.get(resource, { params: params })
				.then(({ data }) => {
					resolve(data);
				})
				.catch((error) => {
					reject(error);
				});
		});
	},

	/**
	 * Send the GET HTTP request
	 * @param resource
	 * @param slug
	 * @returns {*}
	 */
	get(resource, slug = "") {
		return new Promise((resolve, reject) => {
			Vue.axios
				.get(`${resource}/${slug}`)
				.then(({ data }) => {
					resolve(data);
				})
				.catch((error) => {
					reject(error);
				});
		});
	},

	/**
	 * Set the POST HTTP request
	 * @param resource
	 * @param params
	 * @returns {*}
	 */
	post(resource, params) {
		return new Promise((resolve, reject) => {
			Vue.axios
				.post(resource, params)
				.then(({ data }) => {
					resolve(data);
				})
				.catch((error) => {
					reject(error);
				});
		});
	},

	/**
	 * Send the UPDATE HTTP request
	 * @param resource
	 * @param slug
	 * @param params
	 * @returns {IDBRequest<IDBValidKey> | Promise<void>}
	 */
	update(resource, slug, params) {
		return new Promise((resolve, reject) => {
			Vue.axios
				.put(`${resource}/${slug}`, params)
				.then(({ data }) => {
					resolve(data);
				})
				.catch((error) => {
					reject(error);
				});
		});
	},

	/**
	 * Send the PUT HTTP request
	 * @param resource
	 * @param params
	 * @returns {IDBRequest<IDBValidKey> | Promise<void>}
	 */
	put(resource, params) {
		return new Promise((resolve, reject) => {
			Vue.axios
				.put(resource, params)
				.then(({ data }) => {
					resolve(data);
				})
				.catch((error) => {
					reject(error);
				});
		});
	},

	/**
	 * Send the PATCH HTTP request
	 * @param resource
	 * @param params
	 * @returns {IDBRequest<IDBValidKey> | Promise<void>}
	 */
	patch(resource, params) {
		return new Promise((resolve, reject) => {
			Vue.axios
				.patch(resource, params)
				.then(({ data }) => {
					resolve(data);
				})
				.catch((error) => {
					reject(error);
				});
		});
	},

	/**
	 * Send the DELETE HTTP request
	 * @param resource
	 * @returns {*}
	 */
	delete(resource) {
		return new Promise((resolve, reject) => {
			Vue.axios
				.delete(resource)
				.then(({ data }) => {
					resolve(data);
				})
				.catch((error) => {
					reject(error);
				});
		});
	},

	/**
	 * Send the UPLOAD HTTP request
	 * @param resource
	 * @returns {*}
	 */
	upload(resource, params) {
		return new Promise((resolve, reject) => {
			Vue.axios
				.post(resource, params, {
					headers: { "content-type": "multipart/form-data" },
				})
				.then(({ data }) => {
					resolve(data);
				})
				.catch((error) => {
					reject(error);
				});
		});
	},
};

export default ApiService;
